import { FadedScrollerContainer, Scroller } from '@/components/shared';
import {
  getPlatformLabel,
  isMessageFromAsta,
} from '@/utils/variantBasedLabels';
import { Message } from '@/models/Message';
import { optAppConfig } from '@/utils/config';
import { PermissionsContext } from '@/components/context/PermissionsContext';
import { TABLE_ANNOTATION } from 'configs/NoraVariants';
import { Thread } from '@/models/Thread';
import { ThreadFeedback } from '@/components/chat/ThreadFeedback';
import { ThreadFeedbackButtons } from '@/components/chat/ThreadFeedbackButtons';
import MessageBar from '@/components/chat/MessageBar';
import MessageList from '@/components/chat/MessageList';

import { Box, Typography } from '@mui/material';
import { Fragment, useCallback, useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';

const noraExperienceVariant = optAppConfig((_) => _.experience.variant);
const displayName = getPlatformLabel(noraExperienceVariant);

interface MultiToolChatProps {
  thread: Thread;
  onSendMessage: (text: string) => Promise<void>;
  onFilesChanged?: (files: File[]) => void;
  isMessageBarPending: boolean;
}

export const MultiToolChat = ({
  thread,
  isMessageBarPending,
  onSendMessage,
  onFilesChanged,
}: MultiToolChatProps) => {
  const { isReadOnly } = useContext(PermissionsContext);
  const conversationMessages: Message[] = thread.messages;
  const endOfMessagesRef = useRef<HTMLDivElement>(null);

  const latestMessage = conversationMessages[conversationMessages.length - 1];
  // enable the message bar only if the latest message is from Asta or if there is no message
  // this prevents the user from double messaging Asta
  const isLatestMessageFromAsta = latestMessage
    ? isMessageFromAsta(latestMessage)
    : true;

  const scrollToLatestMessage = useCallback(() => {
    endOfMessagesRef.current?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: 'smooth',
    });
  }, [endOfMessagesRef]);

  useEffect(() => {
    if (!isReadOnly) {
      scrollToLatestMessage();
    }
  }, [conversationMessages.length, isReadOnly]);

  return (
    <Layout>
      <LayoutMessages>
        <Scroller>
          <MessageList messages={conversationMessages} />
          <Box ref={endOfMessagesRef} sx={{ mt: 2 }}></Box>
        </Scroller>
      </LayoutMessages>
      {!isReadOnly && (
        <Fragment>
          <LayoutInput>
            <MessageBar
              onSend={onSendMessage}
              onFilesChanged={onFilesChanged}
              isPending={isMessageBarPending || !isLatestMessageFromAsta}
            />
          </LayoutInput>
          <ThreadFeedbackWrapper>
            {noraExperienceVariant === TABLE_ANNOTATION ? (
              <>
                <Typography
                  style={{ fontWeight: 'bold', color: 'hotpink' }}
                  variant="body1"
                  component={'span'}
                >
                  GUI Interaction Feedback:
                </Typography>
                <ThreadFeedbackButtons threadId={thread.id} />
              </>
            ) : (
              <>
                <span>{displayName} only knows about your current thread.</span>
                <ThreadFeedback thread={thread} />
              </>
            )}
          </ThreadFeedbackWrapper>
        </Fragment>
      )}
    </Layout>
  );
};

export const Layout = styled('div')`
  display: grid;
  grid-template-areas:
    'thread-messages'
    'thread-input'
    'thread-feedback';
  grid-template-columns: 1fr auto;
  grid-template-rows: 1fr;
  height: 100%;
`;

export const LayoutMessages = styled(FadedScrollerContainer)`
  grid-area: thread-messages;
`;

const LayoutInput = styled('div')`
  grid-area: thread-input;
  padding-bottom: ${({ theme }) => theme.spacing(1)};
  padding-left: ${({ theme }) => theme.spacing(3)};
  padding-right: ${({ theme }) => theme.spacing(3)};
`;

const ThreadFeedbackWrapper = styled('div')`
  color: #9fbabc;
  font-size: ${({ theme }) => theme.spacing(1.75)};
  grid-area: thread-feedback;
  line-height: ${({ theme }) => theme.spacing(2.625)};
  padding-left: ${({ theme }) => theme.spacing(3)};
  padding-right: ${({ theme }) => theme.spacing(3)};
`;
